import {
  Button,
  getUserIdFromRefreshToken,
  Icon_Kebab,
  Menu,
  Modal,
  useManagedModal,
} from "@shapr3d/frontend-lib";

import { type Comment, useMutateDeleteComment } from "../../hooks/useComments";
import { useTracker } from "../../hooks/useTracker";
import { useViewLinkId, useWebExport } from "../../hooks/useWebExport";

import style from "./CommentContextMenu.module.css";

interface CommentContextMenuProps {
  comment: Comment;
}

export const CommentContextMenu = ({ comment }: CommentContextMenuProps) => {
  const tracker = useTracker();
  const deleteCommentModal = useManagedModal();
  const viewLinkId = useViewLinkId() ?? "";
  const { data: webExportData } = useWebExport();

  const { mutateAsync: mutateDeleteComment, isPending: isMutateDeletePending } =
    useMutateDeleteComment();

  return (
    <>
      <Menu
        className={style.contextMenu}
        activator={
          <Button
            variant="borderless"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Icon_Kebab />
          </Button>
        }
        onOpen={() => {
          tracker.trackCommentContextMenuVisibilityChanged({
            commentId: comment.commentId,
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            visible: true,
          });
        }}
        onClose={() => {
          tracker.trackCommentContextMenuVisibilityChanged({
            commentId: comment.commentId,
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
            visible: false,
          });
        }}
      >
        <Menu.Item
          closeOnClick
          disabled={
            isMutateDeletePending || comment.createdByUserId !== getUserIdFromRefreshToken()
          }
          onClick={async (event) => {
            event.stopPropagation();
            tracker.trackCommentDeleteIntended({
              commentId: comment.commentId,
              viewLinkId,
              webExportId: webExportData?.webExport.webExportId,
            });
            deleteCommentModal.open();
          }}
        >
          <span className={style.red}>Delete comment...</span>
        </Menu.Item>
      </Menu>
      <Modal size="small" title="Delete comment?" {...deleteCommentModal.props}>
        <div className={style.deleteConfirmationContainer}>
          <span>{`This comment will be deleted immediately. You can\u2019t undo this action.`}</span>
          <Button
            variant={"red"}
            disabled={isMutateDeletePending}
            onClick={async () => {
              try {
                await mutateDeleteComment({ commentId: comment.commentId });
              } finally {
                tracker.trackCommentDeleteConfirmed({
                  commentId: comment.commentId,
                  viewLinkId,
                  webExportId: webExportData?.webExport.webExportId,
                });
                deleteCommentModal.close();
              }
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
};
